import type { ThemeConfig } from 'antd';

/**
 * Holodao 高保真深色科技风主题
 * 
 * 基于 Figma 设计稿深度定制：
 * - 核心：极度透明感 + 高强度模糊 + 右上角径向光源
 * - 价格：亮青色 (#00d4ff)
 * - 辅助文字：蓝灰色 (#a0b1c5)
 */

export const colors = {
  // 背景色系
  bgPrimary: '#02040a',
  bgSecondary: '#0f1435',
  bgTertiary: '#141a40',
  bgCard: 'rgba(255, 255, 255, 0.03)',
  bgSidebar: 'rgba(10, 14, 39, 0.7)',
  bgInput: 'rgba(255, 255, 255, 0.05)',
  bgModal: '#0f1435',

  // 主色系
  primary: '#4f6ef7',
  primaryHover: '#6b85ff',
  primaryActive: '#3b5ad6',

  // 强调色
  accent: '#00d4ff', // 亮青色
  accentGlow: 'rgba(0, 212, 255, 0.4)',

  // 文字色
  textPrimary: '#ffffff',
  textSecondary: '#a0b1c5', // 调亮蓝灰色以提升对比度
  textDisabled: 'rgba(255, 255, 255, 0.3)',

  // 边框色
  border: 'rgba(255, 255, 255, 0.15)', // 调亮边框
  borderHover: 'rgba(255, 255, 255, 0.25)',

  // 功能色
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#4f6ef7',
};

export const darkTheme: ThemeConfig = {
  token: {
    colorPrimary: colors.primary,
    colorInfo: colors.info,
    colorSuccess: colors.success,
    colorWarning: colors.warning,
    colorError: colors.error,

    // 关键：背景透明化，由 CSS 的 radial-gradient 决定整体底色
    colorBgContainer: colors.bgCard,
    colorBgElevated: colors.bgModal,
    colorBgLayout: 'transparent',

    // 文字
    colorText: colors.textPrimary,
    colorTextSecondary: colors.textSecondary,
    colorTextDescription: colors.textSecondary,
    colorTextPlaceholder: 'rgba(255, 255, 255, 0.45)', // 大幅调亮占位符文字

    // 边框
    colorBorder: colors.border,
    colorBorderSecondary: colors.border,

    borderRadius: 10,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
  },
  components: {
    Layout: {
      bodyBg: 'transparent',
      headerBg: 'transparent',
      siderBg: colors.bgSidebar,
    },
    Menu: {
      darkItemBg: 'transparent',
      darkItemSelectedBg: 'linear-gradient(90deg, #4f6ef7 0%, #3b5ad6 100%)',
      darkItemColor: 'rgba(255, 255, 255, 0.65)',
      darkItemSelectedColor: '#ffffff',
    },
    Table: {
      headerBg: 'rgba(0, 0, 0, 0.3)',
      headerColor: colors.textSecondary,
      rowHoverBg: 'rgba(79, 110, 247, 0.1)',
      borderColor: 'rgba(255, 255, 255, 0.08)',
    },
    Card: {
      colorBgContainer: colors.bgCard,
      colorBorderSecondary: colors.border,
    },
    Button: {
      colorPrimary: colors.primary,
      borderRadius: 8,
      controlHeight: 36,
    },
    Input: {
      colorBgContainer: colors.bgInput,
      colorBorder: colors.border,
    },
    Select: {
      colorBgContainer: colors.bgInput,
      colorBorder: colors.border,
    },
    Pagination: {
      itemActiveBg: 'rgba(79, 110, 247, 0.2)',
    },
  },
};

export default darkTheme;
